import React, { useEffect, useState, useCallback, useRef } from 'react';
import io from 'socket.io-client';
import request from '../utils/request';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700',
  contacted: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-purple-100 text-purple-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-zinc-100 text-zinc-500',
};

const PAYMENT_COLORS = {
  unpaid: 'bg-zinc-100 text-zinc-500',
  pending: 'bg-amber-100 text-amber-700',
  paid: 'bg-emerald-100 text-emerald-700',
  refunded: 'bg-red-100 text-red-600',
};

const FULFILLMENT_COLORS = {
  pending: 'bg-zinc-100 text-zinc-500',
  open_for_bids: 'bg-blue-100 text-blue-700',
  bids_in_review: 'bg-indigo-100 text-indigo-700',
  assigned: 'bg-purple-100 text-purple-700',
  in_fulfillment: 'bg-amber-100 text-amber-700',
  fulfilled: 'bg-green-100 text-green-700',
};

const BID_STATUS_COLORS = {
  submitted: 'bg-blue-100 text-blue-700',
  under_review: 'bg-indigo-100 text-indigo-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-600',
  withdrawn: 'bg-zinc-100 text-zinc-500',
  expired: 'bg-zinc-100 text-zinc-500',
};

/* ─── Drawer: detail + message thread + marketplace ─── */
const ServiceRequestDrawer = ({ sr, onClose, onUpdated }) => {
  const [messages, setMessages] = useState([]);
  const [providerMessages, setProviderMessages] = useState([]);
  const [msgInput, setMsgInput] = useState('');
  const [providerMsgInput, setProviderMsgInput] = useState('');
  const [sending, setSending] = useState(false);
  const [priceVal, setPriceVal] = useState('');
  const [settingPrice, setSettingPrice] = useState(false);
  const [localSr, setLocalSr] = useState(sr);
  const [partySizeVal, setPartySizeVal] = useState(String(sr.partySize || ''));
  const [savingPartySize, setSavingPartySize] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [messageThread, setMessageThread] = useState('guest');
  const [bids, setBids] = useState([]);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [bidDeadlineVal, setBidDeadlineVal] = useState('');
  const [openingForBids, setOpeningForBids] = useState(false);
  const messagesEndRef = useRef(null);
  const providerMessagesEndRef = useRef(null);
  const socketRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollProviderToBottom = () => {
    providerMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Fetch guest messages
  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await request.get(`/service-requests/${sr._id}/messages`);
      const all = data.messages || [];
      setMessages(all.filter((m) => !m.threadType || m.threadType === 'guest'));
      setProviderMessages(all.filter((m) => m.threadType === 'provider'));
    } catch (_) {}
  }, [sr._id]);

  // Fetch bids
  const fetchBids = useCallback(async () => {
    setBidsLoading(true);
    try {
      const { data } = await request.get(`/admin/service-requests/${sr._id}/bids`);
      setBids(data.bids || []);
    } catch (_) {}
    setBidsLoading(false);
  }, [sr._id]);

  // Mark messages as read by admin
  const markRead = useCallback(async () => {
    try {
      await request.patch(`/service-requests/${sr._id}/messages/read`, { readerType: 'admin' });
    } catch (_) {}
  }, [sr._id]);

  useEffect(() => {
    fetchMessages();
    markRead();
    fetchBids();
  }, [fetchMessages, markRead, fetchBids]);

  useEffect(scrollToBottom, [messages]);
  useEffect(scrollProviderToBottom, [providerMessages]);

  // Socket.IO for real-time
  useEffect(() => {
    const socketOrigin = process.env.REACT_APP_SOCKET_ORIGIN || 'http://localhost:5001';
    const socket = io(socketOrigin, {
      path: '/rt',
      transports: ['websocket'],
      withCredentials: true,
    });

    socket.on('connect', () => {
      socket.emit('serviceRequest:join', { serviceRequestId: sr._id });
    });

    socket.on('serviceRequest:newMessage', (msg) => {
      setMessages((prev) => [...prev, msg]);
      markRead();
    });

    socket.on('serviceRequest:updated', (update) => {
      if (update?.serviceRequest) {
        setLocalSr(update.serviceRequest);
        if (onUpdated) onUpdated();
      }
    });

    socketRef.current = socket;
    return () => {
      socket.emit('serviceRequest:leave', { serviceRequestId: sr._id });
      socket.disconnect();
    };
  }, [sr._id, markRead, onUpdated]);

  const handleSend = async () => {
    if (!msgInput.trim() || sending) return;
    setSending(true);
    try {
      await request.post(`/service-requests/${sr._id}/messages`, {
        content: msgInput.trim(),
        senderType: 'admin',
        senderName: 'Concierge Team',
      });
      setMsgInput('');
      await fetchMessages();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleSetPrice = async () => {
    const value = parseFloat(priceVal);
    if (!value || value <= 0) return;
    setSettingPrice(true);
    try {
      await request.put(`/service-requests/${sr._id}/set-price`, { price: value });
      setPriceVal('');
      if (onUpdated) onUpdated();
      const { data } = await request.get('/service-requests/all');
      const updated = (data.serviceRequests || []).find((r) => r._id === sr._id);
      if (updated) setLocalSr(updated);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to set price');
    } finally {
      setSettingPrice(false);
    }
  };

  const handleUpdatePartySize = async () => {
    const size = parseInt(partySizeVal, 10);
    if (!size || size < 1 || size > 50 || size === localSr.partySize) return;
    setSavingPartySize(true);
    try {
      const { data } = await request.patch(`/service-requests/${sr._id}`, { partySize: size });
      if (data.serviceRequest) setLocalSr(data.serviceRequest);
      if (onUpdated) onUpdated();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to update party size');
      setPartySizeVal(String(localSr.partySize || ''));
    } finally {
      setSavingPartySize(false);
    }
  };

  // Open for bids
  const handleOpenForBids = async () => {
    setOpeningForBids(true);
    try {
      const body = {};
      if (bidDeadlineVal) body.bidDeadline = new Date(bidDeadlineVal).toISOString();
      await request.post(`/admin/service-requests/${sr._id}/open-for-bids`, body);
      if (onUpdated) onUpdated();
      const { data } = await request.get('/service-requests/all');
      const updated = (data.serviceRequests || []).find((r) => r._id === sr._id);
      if (updated) setLocalSr(updated);
      fetchBids();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to open for bids');
    }
    setOpeningForBids(false);
  };

  const handleAcceptBid = async (bidId) => {
    if (!window.confirm('Accept this bid? All other bids will be rejected.')) return;
    try {
      const { data } = await request.post(`/admin/bids/${bidId}/accept`);
      if (data.serviceRequest) setLocalSr(data.serviceRequest);
      fetchBids();
      if (onUpdated) onUpdated();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to accept bid');
    }
  };

  const handleRejectBid = async (bidId) => {
    if (!window.confirm('Reject this bid?')) return;
    try {
      await request.post(`/admin/bids/${bidId}/reject`);
      fetchBids();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to reject bid');
    }
  };

  // Send provider thread message
  const handleSendProviderMsg = async () => {
    if (!providerMsgInput.trim() || sending) return;
    setSending(true);
    try {
      await request.post(`/service-requests/${sr._id}/messages`, {
        content: providerMsgInput.trim(),
        senderType: 'admin',
        senderName: 'Concierge Team',
        threadType: 'provider',
      });
      setProviderMsgInput('');
      await fetchMessages();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  };

  const s = localSr;
  const acceptedBid = bids.find((b) => b.status === 'accepted');
  const margin = (s.price != null && acceptedBid) ? (s.price * 100 - acceptedBid.amount) : null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col animate-slide-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div>
            <h2 className="text-lg font-bold text-zinc-900">
              {(s.servicesRequested || []).join(', ')}
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">{s.email} &middot; {s.linkKey}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-100 text-zinc-500">
            ✕
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-zinc-200">
          {['details', 'marketplace', 'messages'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              {tab === 'details' ? 'Details' : tab === 'marketplace' ? 'Marketplace' : 'Messages'}
            </button>
          ))}
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'details' && (
          <div className="px-5 py-4 space-y-3 border-b border-zinc-100">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[s.status] || STATUS_COLORS.new}`}>
                {s.status}
              </span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PAYMENT_COLORS[s.paymentStatus] || PAYMENT_COLORS.unpaid}`}>
                {s.paymentStatus || 'unpaid'}
              </span>
              {s.fulfillmentStatus && s.fulfillmentStatus !== 'pending' && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FULFILLMENT_COLORS[s.fulfillmentStatus] || 'bg-zinc-100 text-zinc-500'}`}>
                  {s.fulfillmentStatus?.replace(/_/g, ' ')}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Requested Date</p>
                <p className="text-zinc-900 font-medium">{formatDate(s.serviceRequestDate)}</p>
              </div>
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Created</p>
                <p className="text-zinc-900 font-medium">{formatDate(s.createdAt)}</p>
              </div>
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Party Size</p>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={partySizeVal}
                    onChange={(e) => setPartySizeVal(e.target.value)}
                    onBlur={handleUpdatePartySize}
                    onKeyDown={(e) => e.key === 'Enter' && handleUpdatePartySize()}
                    disabled={savingPartySize}
                    className="w-16 px-2 py-1 text-sm font-medium text-zinc-900 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                  />
                  {savingPartySize && <span className="text-xs text-zinc-400">Saving...</span>}
                </div>
              </div>
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Budget</p>
                <p className="text-zinc-900 font-medium">{s.budget ? `$${s.budget}` : '—'}</p>
              </div>
              {s.price != null && (
                <div>
                  <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Price</p>
                  <p className="text-zinc-900 font-bold">${s.price.toFixed(2)}</p>
                </div>
              )}
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Phone</p>
                <p className="text-zinc-900 font-medium">{s.phoneNumber || '—'}</p>
              </div>
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Best Time</p>
                <p className="text-zinc-900 font-medium">{s.bestTimeToCall || '—'}</p>
              </div>
            </div>
            {s.additionalDetails && (
              <div>
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Notes</p>
                <p className="text-zinc-700 text-sm mt-1">{s.additionalDetails}</p>
              </div>
            )}

            {/* Pricing view */}
            {(s.price != null || acceptedBid) && (
              <div className="bg-zinc-50 rounded-lg p-3 space-y-1">
                <p className="text-zinc-400 text-xs font-semibold uppercase tracking-wider">Pricing</p>
                {s.price != null && <p className="text-sm">Guest price: <span className="font-bold">${s.price.toFixed(2)}</span></p>}
                {acceptedBid && <p className="text-sm">Provider bid: <span className="font-bold">${(acceptedBid.amount / 100).toFixed(2)}</span></p>}
                {margin != null && <p className="text-sm">Margin: <span className="font-bold text-green-600">${(margin / 100).toFixed(2)}</span></p>}
              </div>
            )}

            {/* Set price action */}
            {s.paymentStatus !== 'paid' && (
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Price"
                  value={priceVal}
                  onChange={(e) => setPriceVal(e.target.value)}
                  className="w-28 px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={handleSetPrice}
                  disabled={settingPrice || !priceVal}
                  className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {settingPrice ? '...' : 'Set Price'}
                </button>
              </div>
            )}
          </div>
          )}

          {/* Marketplace tab */}
          {activeTab === 'marketplace' && (
          <div className="px-5 py-4 space-y-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${FULFILLMENT_COLORS[s.fulfillmentStatus] || FULFILLMENT_COLORS.pending}`}>
                {(s.fulfillmentStatus || 'pending').replace(/_/g, ' ')}
              </span>
              {s.marketId && (
                <span className="text-xs text-zinc-500">Market: {typeof s.marketId === 'object' ? s.marketId.name : s.marketId}</span>
              )}
            </div>

            {(!s.fulfillmentStatus || s.fulfillmentStatus === 'pending') && (
              <div className="bg-zinc-50 rounded-xl p-4 space-y-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">Open for Bids</h4>
                <div className="flex items-center gap-2">
                  <input
                    type="datetime-local"
                    value={bidDeadlineVal}
                    onChange={(e) => setBidDeadlineVal(e.target.value)}
                    className="px-3 py-2 text-sm border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  />
                  <button
                    onClick={handleOpenForBids}
                    disabled={openingForBids}
                    className="px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {openingForBids ? 'Opening...' : 'Open for Bids'}
                  </button>
                </div>
              </div>
            )}

            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-3">
                Bids ({bids.length})
              </h4>
              {bidsLoading ? (
                <div className="flex justify-center py-4"><div className="w-6 h-6 border-2 border-gray-200 border-t-indigo-600 rounded-full animate-spin" /></div>
              ) : bids.length === 0 ? (
                <p className="text-sm text-zinc-400 text-center py-4">No bids yet</p>
              ) : (
                <div className="space-y-3">
                  {bids.map((bid) => (
                    <div key={bid._id} className="bg-white border border-zinc-200 rounded-xl p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-zinc-900">
                            {bid.providerId?.firstName} {bid.providerId?.lastName}
                          </p>
                          <p className="text-xs text-zinc-500">{bid.providerId?.businessName || bid.providerId?.email}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${BID_STATUS_COLORS[bid.status] || 'bg-zinc-100'}`}>
                          {bid.status}
                        </span>
                      </div>
                      <p className="text-xl font-bold text-zinc-900">${(bid.amount / 100).toFixed(2)}</p>
                      {bid.breakdown?.laborCents && (
                        <p className="text-xs text-zinc-400 mt-1">
                          Labor: ${(bid.breakdown.laborCents / 100).toFixed(2)}
                          {bid.breakdown.materialsCents ? ` | Materials: $${(bid.breakdown.materialsCents / 100).toFixed(2)}` : ''}
                          {bid.breakdown.travelCents ? ` | Travel: $${(bid.breakdown.travelCents / 100).toFixed(2)}` : ''}
                        </p>
                      )}
                      {bid.estimatedDuration && <p className="text-sm text-zinc-600 mt-1">Duration: {bid.estimatedDuration}</p>}
                      {bid.message && <p className="text-sm text-zinc-600 mt-1 italic">"{bid.message}"</p>}
                      {['submitted', 'under_review'].includes(bid.status) && (
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => handleAcceptBid(bid._id)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700">
                            Accept
                          </button>
                          <button onClick={() => handleRejectBid(bid._id)} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-100 text-red-600 hover:bg-red-200">
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Messages tab */}
          {activeTab === 'messages' && (
          <>
          <div className="flex border-b border-zinc-100">
            <button
              onClick={() => setMessageThread('guest')}
              className={`flex-1 px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                messageThread === 'guest' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-zinc-400'
              }`}
            >
              Guest Thread ({messages.length})
            </button>
            <button
              onClick={() => setMessageThread('provider')}
              className={`flex-1 px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${
                messageThread === 'provider' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-zinc-400'
              }`}
            >
              Provider Thread ({providerMessages.length})
            </button>
          </div>
          <div className="px-5 py-4">
            <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
              {messageThread === 'guest' ? (
                <>
                {messages.length === 0 && (
                  <p className="text-zinc-400 text-sm text-center py-6">No guest messages yet</p>
                )}
                {messages.map((msg) => {
                  const isAdmin = msg.senderType === 'admin';
                  return (
                    <div key={msg._id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${isAdmin ? 'bg-indigo-600 text-white rounded-br-md' : 'bg-zinc-100 text-zinc-800 rounded-bl-md'}`}>
                        <p className={`text-[10px] font-semibold mb-0.5 ${isAdmin ? 'text-indigo-200' : 'text-zinc-400'}`}>
                          {msg.senderName}
                        </p>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-[10px] mt-1 ${isAdmin ? 'text-indigo-300' : 'text-zinc-400'}`}>
                          {new Date(msg.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
                </>
              ) : (
                <>
                {providerMessages.length === 0 && (
                  <p className="text-zinc-400 text-sm text-center py-6">No provider messages yet</p>
                )}
                {providerMessages.map((msg) => {
                  const isAdmin = msg.senderType === 'admin';
                  return (
                    <div key={msg._id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${isAdmin ? 'bg-indigo-600 text-white rounded-br-md' : 'bg-emerald-50 text-zinc-800 rounded-bl-md'}`}>
                        <p className={`text-[10px] font-semibold mb-0.5 ${isAdmin ? 'text-indigo-200' : 'text-emerald-600'}`}>
                          {msg.senderName}
                        </p>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-[10px] mt-1 ${isAdmin ? 'text-indigo-300' : 'text-zinc-400'}`}>
                          {new Date(msg.createdAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={providerMessagesEndRef} />
                </>
              )}
            </div>
          </div>
          </>
          )}
        </div>

        {/* Message input -- only show on messages tab */}
        {activeTab === 'messages' && (
        <div className="px-5 py-3 border-t border-zinc-200 bg-white">
          <div className="flex items-center gap-2">
            {messageThread === 'guest' ? (
              <>
              <input
                type="text"
                value={msgInput}
                onChange={(e) => setMsgInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                placeholder="Message guest..."
                className="flex-1 px-4 py-2.5 text-sm border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={handleSend}
                disabled={!msgInput.trim() || sending}
                className="px-4 py-2.5 text-sm font-medium rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                Send
              </button>
              </>
            ) : (
              <>
              <input
                type="text"
                value={providerMsgInput}
                onChange={(e) => setProviderMsgInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendProviderMsg()}
                placeholder="Message provider..."
                className="flex-1 px-4 py-2.5 text-sm border border-emerald-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <button
                onClick={handleSendProviderMsg}
                disabled={!providerMsgInput.trim() || sending}
                className="px-4 py-2.5 text-sm font-medium rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors"
              >
                Send
              </button>
              </>
            )}
          </div>
        </div>
        )}
      </div>
    </>
  );
};

/* ─── Main screen ─── */
const ServiceRequestsScreen = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [sortField, setSortField] = useState('serviceRequestDate');
  const [sortDir, setSortDir] = useState('desc');

  const fetchRequests = useCallback(async () => {
    try {
      setError(null);
      const { data } = await request.get('/service-requests/all');
      setRequests(data.serviceRequests || []);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load service requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortArrow = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'desc' ? ' ↓' : ' ↑';
  };

  const sorted = [...requests].sort((a, b) => {
    const aVal = new Date(a[sortField] || 0).getTime();
    const bVal = new Date(b[sortField] || 0).getTime();
    return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
  });

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center h-screen">
        <div className="w-8 h-8 border-4 border-zinc-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-zinc-50 p-6">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Service Requests</h1>
            <p className="text-sm text-zinc-500 mt-1">
              {requests.length} total request{requests.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={() => { setLoading(true); fetchRequests(); }}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50 transition-colors"
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>
        )}

        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Service</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Property</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Guest</th>
                  <th
                    className="text-left px-4 py-3 font-semibold text-zinc-600 cursor-pointer select-none hover:text-indigo-600 transition-colors"
                    onClick={() => toggleSort('serviceRequestDate')}
                  >
                    Date{sortArrow('serviceRequestDate')}
                  </th>
                  <th
                    className="text-left px-4 py-3 font-semibold text-zinc-600 cursor-pointer select-none hover:text-indigo-600 transition-colors"
                    onClick={() => toggleSort('createdAt')}
                  >
                    Created{sortArrow('createdAt')}
                  </th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Budget</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Status</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Price</th>
                  <th className="text-left px-4 py-3 font-semibold text-zinc-600">Payment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {sorted.map((req) => (
                  <tr
                    key={req._id}
                    onClick={() => setSelectedRequest(req)}
                    className="hover:bg-zinc-50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 font-medium text-zinc-900">
                      {(req.servicesRequested || []).join(', ')}
                    </td>
                    <td className="px-4 py-3 text-zinc-600">{req.linkKey}</td>
                    <td className="px-4 py-3 text-zinc-600">{req.email}</td>
                    <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">
                      {formatDate(req.serviceRequestDate)}
                    </td>
                    <td className="px-4 py-3 text-zinc-500 whitespace-nowrap text-xs">
                      {formatDate(req.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-zinc-600">
                      {req.budget ? `$${req.budget}` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[req.status] || STATUS_COLORS.new}`}>
                        {req.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-zinc-900 font-medium">
                      {req.price != null ? `$${req.price.toFixed(2)}` : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${PAYMENT_COLORS[req.paymentStatus] || PAYMENT_COLORS.unpaid}`}>
                        {req.paymentStatus || 'unpaid'}
                      </span>
                    </td>
                  </tr>
                ))}
                {requests.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center py-12 text-zinc-400">
                      No service requests found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail drawer */}
      {selectedRequest && (
        <ServiceRequestDrawer
          sr={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onUpdated={fetchRequests}
        />
      )}
    </div>
  );
};

export default ServiceRequestsScreen;
